import React, { useState } from 'react';
import MainLayout from '../../components/ui/MainLayout';
import Icon from '../../components/AppIcon';
import { useRBAC } from '../../contexts/RBACContext';
import { useLanguage } from '../../contexts/LanguageContext';
import MealTickets from './components/MealTickets';
import NFCCards from './components/NFCCards';
import MealValidation from './components/MealValidation';
import Cardholders from './components/Cardholders';
import Analytics from './components/Analytics';

const TABS = [
  { id: 'validation', label: 'Meal Validation', icon: 'Scan', adminOnly: false },
  { id: 'tickets', label: 'Meal Tickets', icon: 'Ticket', adminOnly: true },
  { id: 'nfc', label: 'NFC Cards', icon: 'CreditCard', adminOnly: false },
  { id: 'cardholders', label: 'Cardholders', icon: 'Users', adminOnly: false },
  { id: 'analytics', label: 'Reports & Analytics', icon: 'BarChart2', adminOnly: false },
];

export default function Restaurant() {
  const { isAdmin, isViewer, role } = useRBAC();
  const { language } = useLanguage();
  const [activeTab, setActiveTab] = useState('validation');

  const canAccess = isAdmin || isViewer || role === 'restaurant_moderator';
  const visibleTabs = TABS?.filter(t => !t?.adminOnly || isAdmin);

  if (!canAccess) {
    return (
      <MainLayout>
        <div className="flex flex-col items-center justify-center py-24 gap-4">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
            <Icon name="ShieldX" size={28} className="text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-foreground">Access Restricted</h2>
          <p className="text-muted-foreground text-sm">You do not have permission to access the Restaurant module.</p>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-foreground font-heading flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Icon name="UtensilsCrossed" size={20} className="text-emerald-600" />
              </div>
              {language === 'fr' ? 'Restaurant' : 'Restaurant'}
            </h2>
            <p className="text-sm text-muted-foreground mt-1 ml-12">
              {language === 'fr' ?'Gestion des repas, cartes NFC et validation des accès' :'Meal management, NFC cards, and access validation'}
            </p>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground bg-card border border-border rounded-lg px-3 py-2">
            <Icon name="Shield" size={14} className={isAdmin ? 'text-amber-500' : 'text-sky-400'} />
            <span className={`font-semibold ${isAdmin ? 'text-amber-600' : 'text-sky-600'}`}>
              {isAdmin ? 'Full Admin' : 'Moderator'}
            </span>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-border">
          <nav className="flex gap-1 overflow-x-auto pb-px">
            {visibleTabs?.map(tab => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-all ${
                  activeTab === tab?.id
                    ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                }`}
              >
                <Icon name={tab?.icon} size={15} />
                {tab?.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div>
          {activeTab === 'validation' && <MealValidation />}
          {activeTab === 'tickets' && isAdmin && <MealTickets isAdmin={isAdmin} />}
          {activeTab === 'nfc' && <NFCCards isAdmin={isAdmin} />}
          {activeTab === 'cardholders' && <Cardholders isAdmin={isAdmin} />}
          {activeTab === 'analytics' && <Analytics isAdmin={isAdmin} />}
        </div>
      </div>
    </MainLayout>
  );
}
